import React, { useState, useEffect, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Event, Concept, apiService } from '../../services/apiService';
import { Mail, Calendar, User, Tag, AlertCircle, CheckCircle, Clock, Filter, ChevronDown, ChevronUp, FileText, Download, ExternalLink, Users } from 'lucide-react';

interface EventDocument {
  id: string;
  type: 'email' | 'pdf' | 'transcript';
  title: string;
  content: string;
  url?: string;
  metadata?: any;
}

interface ExtendedEvent extends Event {
  document?: EventDocument;
  related_entities: string[];
  concept_category?: string;
  duration?: string;
}

// Document renderer components
const PDFRenderer: React.FC<{ document: EventDocument }> = ({ document }) => {
  if (!document.url) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-800 rounded border-2 border-dashed border-gray-600">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-gray-400">PDF content not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4 bg-gray-750 border-b border-gray-600">
        <h3 className="text-sm font-medium text-gray-200">{document.title}</h3>
        <div className="flex gap-2">
          <a 
            href={document.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center gap-1"
          >
            <ExternalLink className="w-3 h-3" />
            Open
          </a>
          <a 
            href={document.url} 
            download
            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs flex items-center gap-1"
          >
            <Download className="w-3 h-3" />
            Download
          </a>
        </div>
      </div>
      <div className="h-96">
        <iframe
          src={document.url}
          className="w-full h-full border-0"
          title={document.title}
        />
      </div>
    </div>
  );
};

const EmailRenderer: React.FC<{ document: EventDocument }> = ({ document }) => {
  const emailData = document.metadata || {};
  
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-600">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400">Subject:</span>
            <span className="text-gray-200 font-medium">{document.title}</span>
          </div>
          {emailData.from && (
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">From:</span>
              <span className="text-gray-200">{emailData.from}</span>
            </div>
          )}
          {emailData.to && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">To:</span>
              <span className="text-gray-200">{emailData.to}</span>
            </div>
          )}
          {emailData.date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">Date:</span>
              <span className="text-gray-200">{new Date(emailData.date).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>
      <div className="p-4">
        <div className="prose prose-invert max-w-none">
          <div className="whitespace-pre-wrap text-gray-200 leading-relaxed">
            {document.content}
          </div>
        </div>
      </div>
    </div>
  );
};

const TranscriptRenderer: React.FC<{ document: EventDocument }> = ({ document }) => {
  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 p-4 bg-gray-750 border-b border-gray-600">
        <FileText className="w-4 h-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-200">{document.title}</h3>
      </div>
      <div className="p-4 max-h-96 overflow-y-auto">
        <div className="prose prose-invert max-w-none">
          <pre className="whitespace-pre-wrap text-sm text-gray-200 leading-relaxed font-mono bg-gray-900 p-4 rounded">
            {document.content}
          </pre>
        </div>
      </div>
    </div>
  );
};

interface TimelineViewProps {
  events: ExtendedEvent[];
  concepts: Concept[];
  viewMode: 'component' | 'category';
  onEventSelect: (event: ExtendedEvent) => void;
  selectedEvent: ExtendedEvent | null;
  entityIdToNameMap: Map<string, string>;
}

const TimelineView: React.FC<TimelineViewProps> = ({ events, concepts, viewMode, onEventSelect, selectedEvent, entityIdToNameMap }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group events by component or concept category/concept
  const groupedData = useMemo(() => {
    
    if (viewMode === 'component') {
      // Group by component
      const groups: { [key: string]: ExtendedEvent[] } = {};
      
      events.forEach(event => {
        const entityIds = event.related_entities || [];
        
        if (entityIds.length === 0) {
          // If no specific components, use "Site-wide" as default
          if (!groups['Site-wide']) {
            groups['Site-wide'] = [];
          }
          groups['Site-wide'].push(event);
        } else {
          entityIds.forEach(entityId => {
            // Use entity name if available, otherwise use ID
            const entityName = entityIdToNameMap.get(entityId) || `Entity ${entityId}`;
            
            if (!groups[entityName]) {
              groups[entityName] = [];
            }
            groups[entityName].push(event);
          });
        }
      });
      

      // Sort events within groups by date
      Object.keys(groups).forEach(key => {
        groups[key].sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
      });

      return { type: 'component', groups };
    } else {
      // Group by concept categories and concepts
      const categories: { [categoryName: string]: { [conceptName: string]: ExtendedEvent[] } } = {};
      
      events.forEach(event => {
        const categoryName = event.concept_category || 'Operational';
        const conceptName = event.event_type || 'General';
        
        if (!categories[categoryName]) {
          categories[categoryName] = {};
        }
        if (!categories[categoryName][conceptName]) {
          categories[categoryName][conceptName] = [];
        }
        categories[categoryName][conceptName].push(event);
      });

      // Sort events within concepts by date
      Object.keys(categories).forEach(categoryName => {
        Object.keys(categories[categoryName]).forEach(conceptName => {
          categories[categoryName][conceptName].sort((a, b) => 
            new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
          );
        });
      });

      return { type: 'category', categories };
    }
  }, [events, viewMode, entityIdToNameMap]);

  // Calculate timeline bounds
  const { minDate, maxDate } = useMemo(() => {
    if (events.length === 0) return { minDate: new Date(), maxDate: new Date() };
    
    const dates = events.map(e => new Date(e.event_date).getTime());
    return {
      minDate: new Date(Math.min(...dates)),
      maxDate: new Date(Math.max(...dates))
    };
  }, [events]);

  const getXPosition = (date: string) => {
    const d = new Date(date).getTime();
    const total = maxDate.getTime() - minDate.getTime();
    const offset = d - minDate.getTime();
    return (offset / total) * 100;
  };

  const toggleGroup = (group: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(group)) {
      newExpanded.delete(group);
    } else {
      newExpanded.add(group);
    }
    setExpandedGroups(newExpanded);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'bg-accent-red';
      case 'high': return 'bg-accent-red';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      case 'info': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: { [key: string]: string } = {
      'Maintenance': 'border-blue-400',
      'Performance': 'border-orange-400',
      'Safety': 'border-accent-red',
      'Environmental': 'border-green-400',
      'System': 'border-purple-400',
      'Operational': 'border-cyan-400',
      'Equipment': 'border-yellow-400'
    };
    return colors[eventType] || 'border-gray-400';
  };

  // Generate month markers
  const monthMarkers = useMemo(() => {
    const markers = [];
    const start = new Date(minDate);
    start.setDate(1);
    
    while (start <= maxDate) {
      markers.push({
        date: new Date(start),
        position: getXPosition(start.toISOString())
      });
      start.setMonth(start.getMonth() + 1);
    }
    
    return markers;
  }, [minDate, maxDate]);

  return (
    <div className="h-full overflow-auto bg-gray-900 p-4">
      {/* Timeline header with month markers */}
      <div className="mb-4 relative h-8 bg-gray-800 rounded border border-gray-700">
        {monthMarkers.map((marker, i) => (
          <div
            key={i}
            className="absolute top-0 h-full border-l border-gray-600"
            style={{ left: `${marker.position}%` }}
          >
            <span className="absolute -top-6 text-xs text-gray-400 whitespace-nowrap transform -translate-x-1/2">
              {marker.date.toLocaleDateString('en', { month: 'short', year: '2-digit' })}
            </span>
          </div>
        ))}
      </div>

      {/* Grouped timelines */}
      <div className="space-y-4">
        {viewMode === 'component' && groupedData.type === 'component' && groupedData.groups && 
          Object.entries(groupedData.groups).sort().map(([group, groupEvents]) => (
            <div key={group} className="bg-gray-800 rounded-lg border border-gray-700">
              {/* Group header */}
              <button
                onClick={() => toggleGroup(group)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-200">
                    Component: {group}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({groupEvents.length} events)
                  </span>
                </div>
                {expandedGroups.has(group) ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {/* Timeline scatter plot */}
              <div className="relative h-16 mx-4 mb-2">
                {/* Timeline line */}
                <div className="absolute top-1/2 w-full h-px bg-gray-600 transform -translate-y-1/2" />
                
                {/* Event dots */}
                {groupEvents.map(event => (
                  <button
                    key={event.event_id}
                    onClick={() => onEventSelect(event)}
                    className={`absolute transform -translate-y-1/2 top-1/2 w-3 h-3 rounded-full border-2 hover:scale-150 transition-transform cursor-pointer ${
                      selectedEvent?.event_id === event.event_id ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-800' : ''
                    } ${getSeverityColor(event.severity)} ${getEventTypeColor(event.event_type)}`}
                    style={{ left: `${getXPosition(event.event_date)}%` }}
                    title={`${event.title || event.event_name}\n${new Date(event.event_date).toLocaleDateString()}`}
                  />
                ))}
              </div>

              {/* Expanded details */}
              {expandedGroups.has(group) && (
                <div className="px-4 pb-4 space-y-2 max-h-60 overflow-y-auto">
                  {groupEvents.map(event => (
                    <button
                      key={event.event_id}
                      onClick={() => onEventSelect(event)}
                      className={`w-full text-left p-3 rounded border transition-colors ${
                        selectedEvent?.event_id === event.event_id
                          ? 'bg-blue-900 border-blue-600'
                          : 'bg-gray-750 border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-200">
                              {event.title || event.event_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-400">
                            <span>{new Date(event.event_date).toLocaleDateString()}</span>
                            <span>{event.event_type}</span>
                            <span className={`px-2 py-0.5 rounded ${getSeverityColor(event.severity)}`}>
                              {event.severity}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))
        }

        {viewMode === 'category' && groupedData.type === 'category' && groupedData.categories && 
          Object.entries(groupedData.categories).sort().map(([categoryName, concepts]) => (
            <div key={categoryName} className="bg-gray-800 rounded-lg border border-gray-700">
              {/* Category header */}
              <button
                onClick={() => toggleGroup(categoryName)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-750 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-200">
                    {categoryName.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-400">
                    ({Object.values(concepts).flat().length} events)
                  </span>
                </div>
                {expandedGroups.has(categoryName) ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {/* Category timeline - all concepts combined */}
              <div className="relative h-16 mx-4 mb-2">
                {/* Timeline line */}
                <div className="absolute top-1/2 w-full h-px bg-gray-600 transform -translate-y-1/2" />
                
                {/* Event dots for all concepts in this category */}
                {Object.values(concepts).flat().map(event => (
                  <button
                    key={event.event_id}
                    onClick={() => onEventSelect(event)}
                    className={`absolute transform -translate-y-1/2 top-1/2 w-3 h-3 rounded-full border-2 hover:scale-150 transition-transform cursor-pointer ${
                      selectedEvent?.event_id === event.event_id ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-gray-800' : ''
                    } ${getSeverityColor(event.severity)} ${getEventTypeColor(event.event_type)}`}
                    style={{ left: `${getXPosition(event.event_date)}%` }}
                    title={`${event.title || event.event_name}\n${new Date(event.event_date).toLocaleDateString()}`}
                  />
                ))}
              </div>

              {/* Expanded concepts */}
              {expandedGroups.has(categoryName) && (
                <div className="px-4 pb-4 space-y-3">
                  {Object.entries(concepts).map(([conceptName, conceptEvents]) => (
                    <div key={conceptName} className="bg-gray-750 rounded border border-gray-600">
                      <div className="px-3 py-2 border-b border-gray-600">
                        <span className="text-sm text-gray-300 font-medium">
                          {conceptName} ({conceptEvents.length})
                        </span>
                      </div>
                      <div className="p-2 space-y-1">
                        {conceptEvents.map(event => (
                          <button
                            key={event.event_id}
                            onClick={() => onEventSelect(event)}
                            className={`w-full text-left p-2 rounded text-xs transition-colors ${
                              selectedEvent?.event_id === event.event_id
                                ? 'bg-blue-900 border-blue-600'
                                : 'hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-gray-200">{event.title || event.event_name}</span>
                              <span className="text-gray-400">{new Date(event.event_date).toLocaleDateString()}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        }
      </div>
    </div>
  );
};

interface EventViewerProps {
  event: ExtendedEvent | null;
  onEntitySelect: (entities: string[]) => void;
  entityIdToNameMap: Map<string, string>;
}

const EventViewer: React.FC<EventViewerProps> = ({ event, onEntitySelect, entityIdToNameMap }) => {
  if (!event) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900 text-gray-400">
        <div className="text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Select an event to view details</p>
        </div>
      </div>
    );
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return <AlertCircle className="w-4 h-4 text-accent-red" />;
      case 'high': return <AlertCircle className="w-4 h-4 text-accent-red" />;
      case 'medium': return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'low': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'info': return <CheckCircle className="w-4 h-4 text-blue-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const handleEntityClick = (entity: string) => {
    onEntitySelect([entity]);
  };

  const handleAllEntitiesClick = () => {
    onEntitySelect(event.related_entities);
  };

  return (
    <div className="h-full overflow-auto bg-gray-900">
      {/* Event header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-start justify-between mb-3">
          <h2 className="text-lg font-medium text-gray-100">{event.title || event.event_name}</h2>
          <div className="flex items-center gap-2">
            {getSeverityIcon(event.severity)}
            <span className={`text-xs px-2 py-1 rounded ${
              event.severity === 'critical' ? 'bg-accent-red/20 text-accent-red' :
              event.severity === 'high' ? 'bg-accent-red/20 text-accent-red' :
              event.severity === 'medium' ? 'bg-yellow-900 text-yellow-200' :
              event.severity === 'low' ? 'bg-green-900 text-green-200' :
              'bg-blue-900 text-blue-200'
            }`}>
              {event.severity?.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400">Date:</span>
            <span className="text-gray-200">
              {new Date(event.event_date).toLocaleString()}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400">Type:</span>
            <span className="px-2 py-0.5 bg-gray-700 text-gray-200 rounded text-xs">
              {event.event_type}
            </span>
          </div>

          {event.concept_category && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400">Category:</span>
              <span className="px-2 py-0.5 bg-gray-700 text-gray-200 rounded text-xs">
                {event.concept_category.replace('_', ' ')}
              </span>
            </div>
          )}

          {event.related_entities && event.related_entities.length > 0 && (
            <div className="flex items-start gap-2">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-gray-400">Affected Components:</span>
              <div className="flex flex-wrap gap-1">
                {event.related_entities.map(entityId => {
                  const entityName = entityIdToNameMap.get(entityId) || entityId;
                  return (
                    <button
                      key={entityId}
                      onClick={() => handleEntityClick(entityId)}
                      className="px-2 py-0.5 bg-blue-900 text-blue-200 rounded text-xs hover:bg-blue-800 transition-colors cursor-pointer"
                      title={`Entity ID: ${entityId}`}
                    >
                      {entityName}
                    </button>
                  );
                })}
              </div>
              {event.related_entities.length > 1 && (
                <button
                  onClick={handleAllEntitiesClick}
                  className="ml-2 px-2 py-0.5 bg-green-900 text-green-200 rounded text-xs hover:bg-green-800 transition-colors"
                >
                  Select All
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            {event.status && (
              <span className={`text-xs px-2 py-0.5 rounded ${
                event.status === 'resolved' ? 'bg-green-900 text-green-200' :
                event.status === 'open' ? 'bg-yellow-900 text-yellow-200' :
                'bg-gray-700 text-gray-200'
              }`}>
                {event.status.toUpperCase()}
              </span>
            )}

            {event.duration && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400 text-xs">Duration: {event.duration}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Event content */}
      <div className="p-6">
        <div className="space-y-4">
          {event.summary && (
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-2">Summary</h3>
              <div className="text-gray-200 text-sm">
                {event.summary}
              </div>
            </div>
          )}
          
          {event.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-2">Description</h3>
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-gray-200 leading-relaxed text-sm">
                  {event.description}
                </div>
              </div>
            </div>
          )}

          {event.document && (
            <div>
              <h3 className="text-sm font-medium text-gray-300 mb-3">Related Document</h3>
              {event.document.type === 'email' && <EmailRenderer document={event.document} />}
              {event.document.type === 'pdf' && <PDFRenderer document={event.document} />}
              {event.document.type === 'transcript' && <TranscriptRenderer document={event.document} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SiteCommunicationsTab: React.FC = () => {
  const { state, setSelectedComponents, entities } = useAppContext();
  const selectedComponents = state.selectedComponents;
  const [events, setEvents] = useState<ExtendedEvent[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<ExtendedEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ExtendedEvent | null>(null);
  const [viewMode, setViewMode] = useState<'component' | 'category'>('category');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  // Create a mapping of entity IDs to entity names for display purposes
  const entityIdToNameMap = useMemo(() => {
    const map = new Map<string, string>();
    entities.forEach(entity => {
      // Use both possible ID fields for compatibility
      const entityId = entity.entity_id || entity.id;
      const entityName = entity.entity_name || entity.name;
      if (entityId && entityName) {
        map.set(entityId, entityName);
      }
    });
    return map;
  }, [entities]);

  // Load events and concepts from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load events for the selected site
        const siteId = state.selectedSite;
        if (siteId) {
          const eventsData = await apiService.getEvents(siteId);
          
          // Transform events to ExtendedEvent format with proper entity handling
          const extendedEvents: ExtendedEvent[] = eventsData.map(event => {
            let relatedEntities: string[] = [];
            
            // Handle different formats of affected_entities
            if (event.affected_entities) {
              if (Array.isArray(event.affected_entities)) {
                // New format: array of entity objects
                relatedEntities = event.affected_entities.map((entity: any) => {
                  if (typeof entity === 'string') {
                    return entity; // Legacy string format
                  } else if (typeof entity === 'object') {
                    // New object format - prefer entity_name, fallback to entity_id
                    return entity.entity_name || entity.entity_id || entity.id || 'Unknown Entity';
                  }
                  return String(entity);
                }).filter(Boolean);
              } else if (typeof event.affected_entities === 'string') {
                try {
                  // Try to parse as JSON if it's a string
                  relatedEntities = JSON.parse(event.affected_entities);
                } catch {
                  // If not JSON, treat as single entity or comma-separated
                  relatedEntities = (event.affected_entities as string).split(',').map((s: string) => s.trim());
                }
              } else if (typeof event.affected_entities === 'object' && event.affected_entities !== null) {
                // Handle object format - look for common entity reference patterns
                const entityObj = event.affected_entities as any;
                if (entityObj.entity_ids) {
                  relatedEntities = entityObj.entity_ids;
                } else if (entityObj.entities) {
                  relatedEntities = entityObj.entities;
                } else {
                  // Extract any string values from the object
                  relatedEntities = Object.values(entityObj)
                    .filter(val => typeof val === 'string') as string[];
                }
              }
            }
            
            
            return {
              ...event,
              related_entities: relatedEntities,
              concept_category: event.event_type || 'Operational'
            };
          });
          
          setEvents(extendedEvents);
          // Set empty concepts array for now
          setConcepts([]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [state.selectedSite]);

  // Handle entity selection from EventViewer
  const handleEntitySelect = (entities: string[]) => {
    setSelectedComponents(entities);
  };

  // Filter events based on criteria
  useEffect(() => {
    let filtered = [...events];

    // Filter by selected components if any
    if (selectedComponents.length > 0) {
      filtered = filtered.filter(event => 
        event.related_entities.some(entity => selectedComponents.includes(entity))
      );
    }

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = filtered.filter(event => event.concept_category === filterCategory);
    }

    // Filter by severity
    if (filterSeverity !== 'all') {
      filtered = filtered.filter(event => event.severity === filterSeverity);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(event => event.status === filterStatus);
    }

    setFilteredEvents(filtered);
  }, [events, selectedComponents, filterCategory, filterSeverity, filterStatus]);

  // Get unique categories and severities from events
  const categories = useMemo(() => {
    const cats = new Set(events.map(e => e.concept_category).filter(Boolean));
    return Array.from(cats).sort();
  }, [events]);

  const severities = useMemo(() => {
    const sevs = new Set(events.map(e => e.severity).filter(Boolean));
    return Array.from(sevs).sort();
  }, [events]);

  const statuses = useMemo(() => {
    const stats = new Set(events.map(e => e.status).filter(Boolean));
    return Array.from(stats).sort();
  }, [events]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header with filters */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-100">Site Communications</h1>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">
              {filteredEvents.length} of {events.length} events
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">Filters:</span>
          </div>

          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as 'component' | 'category')}
            className="px-3 py-1 bg-gray-700 text-gray-200 rounded border border-gray-600 text-sm"
          >
            <option value="category">View by Category</option>
            <option value="component">View by Component</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1 bg-gray-700 text-gray-200 rounded border border-gray-600 text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat?.replace('_', ' ')}
              </option>
            ))}
          </select>

          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-3 py-1 bg-gray-700 text-gray-200 rounded border border-gray-600 text-sm"
          >
            <option value="all">All Severities</option>
            {severities.map(severity => (
              <option key={severity} value={severity}>
                {severity}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1 bg-gray-700 text-gray-200 rounded border border-gray-600 text-sm"
          >
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Timeline view */}
        <div className="flex-1 overflow-hidden">
          <TimelineView
            events={filteredEvents}
            concepts={concepts}
            viewMode={viewMode}
            onEventSelect={setSelectedEvent}
            selectedEvent={selectedEvent}
            entityIdToNameMap={entityIdToNameMap}
          />
        </div>

        {/* Event viewer */}
        <div className="w-1/2 border-l border-gray-700 overflow-hidden">
          <EventViewer 
            event={selectedEvent} 
            onEntitySelect={handleEntitySelect} 
            entityIdToNameMap={entityIdToNameMap}
          />
        </div>
      </div>
    </div>
  );
};